import { useRef } from 'react'

import { PageStoreContext } from './PageStoreContext'

import type { StoreApi } from 'zustand'
import type { BaseUIState, PageStoreState } from './types'

interface PageStoreProviderProps<
  TForm extends Record<string, any>,
  TPrefs extends Record<string, any>,
  TUI extends BaseUIState,
> {
  createStore: () => StoreApi<PageStoreState<TForm, TPrefs, TUI>>
  children: React.ReactNode
}

/**
 * 页面级 Store Provider
 *
 * 通过 Context 将 store 实例注入到页面组件树，确保：
 * - 同一页面实例只创建一次 store（useRef 保证）
 * - 不同页面互不干扰（各自独立的 Context Provider）
 * - 支持同一 store 定义在不同页面复用（如 Music/Rap 共用 GeneratorForm）
 *
 * @example
 * ```tsx
 * <PageStoreProvider createStore={createCoverStore}>
 *   <CoverForm />
 * </PageStoreProvider>
 * ```
 */
export function PageStoreProvider<
  TForm extends Record<string, any>,
  TPrefs extends Record<string, any>,
  TUI extends BaseUIState,
>({ createStore, children }: PageStoreProviderProps<TForm, TPrefs, TUI>) {
  const storeRef = useRef<StoreApi<PageStoreState<TForm, TPrefs, TUI>> | null>(
    null,
  )
  if (!storeRef.current) {
    storeRef.current = createStore()
  }

  return (
    <PageStoreContext.Provider value={storeRef.current}>
      {children}
    </PageStoreContext.Provider>
  )
}
